/*
  Wakeboss Dynamic Shopify Product Lookup Client
  Version: 0.2

  Purpose:
  - Collect recommended SKUs from a Wakeboss Mercury/MerCruiser lookup result.
  - Call a dynamic Shopify product lookup endpoint.
  - Merge the live Shopify product status into the result builder output.
  - Keep Dealer Verify as the highest-priority action.

  Production endpoint:
    /apps/wakeboss-lookup/products

  Contract:
    wakeboss-dynamic-shopify-product-lookup-api-contract-v0.2.json
*/

(function () {
  'use strict';

  class WakebossDynamicProductLookupClient {
    constructor(options = {}) {
      this.endpoint = options.endpoint || '/apps/wakeboss-lookup/products';
      this.timeoutMs = options.timeoutMs || 8000;
      this.maxSkusPerRequest = options.maxSkusPerRequest || 100;
      this.debug = Boolean(options.debug);
    }

    normalizeSku(value) {
      if (!value) return '';

      const compact = String(value).trim().toUpperCase().replace(/\s+/g, '');

      const knownPrefixes = [
        '3302-', '35-', '46-', '47-', '48-', '57-', '97-', '98-', '33-', '26-', '27-',
        '30-', '91-', '89-', '64-', '53-', '21-', '23-', '25-', '18-', '19-', '17-',
        '16-', '15-', '14-', '13-', '12-', '11-', '10-', '6-', '5-'
      ];

      for (const prefix of knownPrefixes) {
        if (compact.startsWith(prefix)) {
          return compact.slice(prefix.length);
        }
      }

      return compact;
    }

    collectSkus(result) {
      const skus = new Set();

      (result?.recommended_sections || []).forEach((section) => {
        (section.items || []).forEach((item) => {
          const candidates = [
            item.wakeboss_sku,
            item.core_part_number,
            item.part_number,
            item.source_part_number,
            item.part_number_display
          ];

          candidates.forEach((candidate) => {
            const normalized = this.normalizeSku(candidate);
            if (normalized) skus.add(normalized);
          });
        });
      });

      return Array.from(skus).sort();
    }

    buildLookupContext(result, extraContext = {}) {
      return {
        source: extraContext.source || 'mercury_serial_lookup',
        entered_serial: result?.input_context?.entered_serial || null,
        matched_identity_id: result?.matched_identity?.master_identity_id || null,
        fitment_ids: this.collectFitmentIds(result),
        customer_session_id: extraContext.customer_session_id || null,
        page_url: extraContext.page_url || (typeof window !== 'undefined' ? window.location.href : null)
      };
    }

    collectFitmentIds(result) {
      const ids = new Set();

      (result?.recommended_sections || []).forEach((section) => {
        (section.fitment_ids || []).forEach((id) => {
          if (id) ids.add(id);
        });

        (section.items || []).forEach((item) => {
          if (item.fitment_id) ids.add(item.fitment_id);
        });
      });

      return Array.from(ids).sort();
    }

    chunkSkus(skus) {
      const chunks = [];

      for (let i = 0; i < skus.length; i += this.maxSkusPerRequest) {
        chunks.push(skus.slice(i, i + this.maxSkusPerRequest));
      }

      return chunks;
    }

    async lookupSkus(skus, lookupContext = {}) {
      const cleanSkus = Array.from(
        new Set((skus || []).map((sku) => this.normalizeSku(sku)).filter(Boolean))
      ).sort();

      if (!cleanSkus.length) {
        return {
          schema_version: '0.1',
          generated_at: new Date().toISOString(),
          records: {},
          warnings: ['No SKUs supplied for dynamic product lookup.']
        };
      }

      const chunks = this.chunkSkus(cleanSkus);
      const merged = {
        schema_version: '0.1',
        generated_at: new Date().toISOString(),
        records: {},
        warnings: []
      };

      for (const chunk of chunks) {
        const response = await this.fetchSkuChunk(chunk, lookupContext);

        Object.assign(merged.records, response.records || {});

        (response.warnings || []).forEach((warning) => {
          if (!merged.warnings.includes(warning)) merged.warnings.push(warning);
        });
      }

      return merged;
    }

    async fetchSkuChunk(skus, lookupContext = {}) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      let timeout = null;

      if (controller) {
        timeout = setTimeout(() => controller.abort(), this.timeoutMs);
      }

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            schema_version: '0.1',
            skus,
            lookup_context: lookupContext
          }),
          signal: controller ? controller.signal : undefined
        });

        if (!response.ok) {
          throw new Error(`Dynamic product lookup failed with HTTP ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        if (this.debug) {
          console.warn('[WakebossDynamicProductLookupClient] lookup failed', error);
        }

        return this.buildFallbackResponse(skus, error);
      } finally {
        if (timeout) clearTimeout(timeout);
      }
    }

    buildFallbackResponse(skus, error) {
      const records = {};

      (skus || []).forEach((sku) => {
        const normalized = this.normalizeSku(sku);

        records[normalized] = {
          raw_sku: sku,
          normalized_sku: normalized,
          exists: false,
          matched_shopify: false,
          match_method: 'none',
          commerce_status: 'lookup_error',
          recommendation_status: 'special_order',
          customer_action: 'request_part',
          button_label: 'Special Order This Part',
          shopify_product: null,
          warnings: [
            'Live product lookup failed. Showing Special Order fallback.'
          ]
        };
      });

      return {
        schema_version: '0.1',
        generated_at: new Date().toISOString(),
        records,
        warnings: [
          error && error.message ? error.message : 'Live product lookup failed.'
        ]
      };
    }

    async applyToResult(result, resultBuilder, extraContext = {}) {
      if (!result || !resultBuilder) {
        throw new Error('applyToResult requires both a lookup result and result builder instance.');
      }

      const skus = this.collectSkus(result);
      const lookupContext = this.buildLookupContext(result, extraContext);
      const liveProductLookup = await this.lookupSkus(skus, lookupContext);

      if (typeof resultBuilder.applyLiveProductLookupToResult === 'function') {
        return resultBuilder.applyLiveProductLookupToResult(result, liveProductLookup);
      }

      throw new Error('Result builder does not support applyLiveProductLookupToResult(). Use wb-mercury-result-builder-v0.7.js or newer.');
    }
  }

  window.WakebossDynamicProductLookupClient = WakebossDynamicProductLookupClient;
})();
